/**
 * Typed email errors for structured error handling.
 *
 * Each error class maps to a specific failure mode in the
 * email pipeline, enabling callers to react appropriately.
 */

export class EmailError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'EmailError';
  }
}

export class ProviderUnavailableError extends EmailError {
  constructor(provider: string) {
    super(`Email provider "${provider}" is unavailable.`, 'provider_unavailable');
    this.name = 'ProviderUnavailableError';
  }
}

export class TemplateMissingError extends EmailError {
  constructor(template: string) {
    super(`Email template "${template}" not found.`, 'template_missing');
    this.name = 'TemplateMissingError';
  }
}

export class InvalidRecipientError extends EmailError {
  constructor(recipient: string) {
    super(`Invalid email recipient: ${recipient}`, 'invalid_recipient');
    this.name = 'InvalidRecipientError';
  }
}

export class QueueFullError extends EmailError {
  constructor(maxSize: number) {
    super(`Email queue is full (max ${maxSize} items).`, 'queue_full');
    this.name = 'QueueFullError';
  }
}

export class RenderingFailedError extends EmailError {
  constructor(template: string, reason: string) {
    super(`Failed to render template "${template}": ${reason}`, 'rendering_failed');
    this.name = 'RenderingFailedError';
  }
}

export class DeliveryTimeoutError extends EmailError {
  constructor(timeoutMs: number) {
    super(`Email delivery timed out after ${timeoutMs}ms.`, 'delivery_timeout');
    this.name = 'DeliveryTimeoutError';
  }
}

export class RetryExceededError extends EmailError {
  constructor(maxRetries: number) {
    super(`Max retries (${maxRetries}) exceeded for email delivery.`, 'retry_exceeded');
    this.name = 'RetryExceededError';
  }
}

export function isEmailError(error: unknown): error is EmailError {
  return error instanceof EmailError;
}
